import React from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import store from 'store/store';
import { ICourseConfigItems } from 'store/reducers/courseConfig';
import { IConceptLinkerAiLinkerItem } from 'types/conceptLinkerAiLinker';

const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

function getDialectCode(courseId: number, dialectId: string): string {
  const courseConfig = (store.getState().courseConfig as ICourseConfigItems)[courseId];
  if (!courseConfig) return dialectId;
  const dialect = courseConfig.targetDialects.find((d) => d.dialectId === parseInt(dialectId, 10));
  return dialect?.dialectCode || dialectId;
}

const AiLinkerAiResultRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IConceptLinkerAiLinkerItem;

  if (!data || !data.ai_result) {
    return null;
  }

  const dialects = data.ai_result.dialects;
  if (!dialects || Object.keys(dialects).length === 0) {
    return null;
  }

  const dialectEntries = Object.entries(dialects);

  return (
    <ResultContainer>
      {dialectEntries.map(([dialectId, dialectResult], idx) => {
        const coveragePercent = Math.round(dialectResult.coverage * 100);
        const hasUnlinked = dialectResult.unlinked_positions && dialectResult.unlinked_positions.length > 0;
        const reasoning = dialectResult.reasoning ? decodeHtmlEntities(dialectResult.reasoning) : null;

        return (
          <DialectResult key={dialectId}>
            {idx > 0 && <DialectSeparator />}
            <ResultHeader>
              <DialectLabel>{getDialectCode(data.course_id, dialectId)}</DialectLabel>
              <CoverageBadge $coverage={coveragePercent}>
                {coveragePercent}%
              </CoverageBadge>
              {hasUnlinked && (
                <UnlinkedCount>
                  {dialectResult.unlinked_positions.length} unlinked
                </UnlinkedCount>
              )}
            </ResultHeader>
            {reasoning && (
              <ReasoningText>{reasoning}</ReasoningText>
            )}
          </DialectResult>
        );
      })}
    </ResultContainer>
  );
};

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 1px 0;
`;

const DialectResult = styled.div``;

const DialectSeparator = styled.div`
  border-top: 1px solid #e0e0e0;
  margin: 2px 0;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DialectLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #7b1fa2;
  background: #f3e5f5;
  border: 1px solid #ce93d8;
  border-radius: 3px;
  padding: 0 4px;
  line-height: 1.6;
`;

const CoverageBadge = styled.span<{ $coverage: number }>`
  display: inline-flex;
  align-items: center;
  padding: 0px 5px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  background-color: ${(props) => {
    if (props.$coverage >= 100) return '#e8f5e9';
    if (props.$coverage >= 80) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    if (props.$coverage >= 100) return '#2e7d32';
    if (props.$coverage >= 80) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid
    ${(props) => {
      if (props.$coverage >= 100) return '#a5d6a7';
      if (props.$coverage >= 80) return '#ffcc80';
      return '#ef9a9a';
    }};
`;

const UnlinkedCount = styled.span`
  color: #ff7043;
  font-size: 12px;
  font-weight: 500;
`;

const ReasoningText = styled.div`
  color: #666;
  font-size: 16px;
  line-height: 1.4;
  white-space: normal;
`;

export default AiLinkerAiResultRenderer;
